/**
 * Two pages side by side, turned by swiping: the conversation on the left,
 * Horton's face on the right.
 *
 * Owner ask (2026-09-04): on a phone the way back to the face was a button
 * nobody found, so this makes the two surfaces behave like pages of one book.
 * Swipe right-to-left and the face comes back; swipe left-to-right and the
 * conversation does. Each page also carries a tab on its shared edge — the
 * conversation's on the right, the face's on the left — so the way across is
 * visible before anyone knows to swipe, and reachable with a keyboard.
 *
 * Only the track moves, and only in X. The conversation page is a plain flex
 * column, so its own scrolling, its modals and anything positioned `fixed`
 * inside it work exactly as they do when the face is off; `touch-action:
 * pan-y` leaves vertical scrolling to the browser and hands us horizontal
 * motion, which is what lets the same finger scroll a long reply and turn the
 * page without the two fighting.
 *
 * With `face` null the pager is one page and no gesture — the transcript,
 * unchanged, which is what every non-lifecoach engine sees.
 */
import { useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, MessageSquareText, ScanFace } from 'lucide-react'

export type FacePagerPage = 'chat' | 'face'

const PAGES: readonly FacePagerPage[] = ['chat', 'face']

const TURN_MS = 320
const TURN_EASE = 'cubic-bezier(0.22, 0.8, 0.24, 1)'
/** Movement before we decide a touch is a swipe and not a scroll or a tap. */
const AXIS_LOCK_PX = 10
/** Past this fraction of the width, releasing turns the page. */
const TURN_FRACTION = 0.22
/** Or this fast, in px per ms — a flick turns it from anywhere. */
const TURN_VELOCITY = 0.45
/** How much of a drag past the last page shows through, as resistance. */
const RUBBER = 0.3
/** A tap that lands right after a swipe is the same finger letting go. */
const CLICK_QUIET_MS = 350

function reducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { return false }
}

interface Drag {
  id: number
  x0: number
  y0: number
  dx: number
  axis: 'undecided' | 'x'
  /** Recent positions, for the release velocity. */
  trail: { x: number; t: number }[]
}

interface Props {
  page: FacePagerPage
  onPage: (page: FacePagerPage) => void
  /** The face page. Null turns the pager into a single, gesture-free page. */
  face: ReactNode | null
  /** The conversation page. */
  children: ReactNode
}

export function HortonFacePager({ page, onPage, face, children }: Props) {
  const two = face !== null
  const index = two ? PAGES.indexOf(page) : 0

  const viewport = useRef<HTMLDivElement | null>(null)
  const track = useRef<HTMLDivElement | null>(null)
  const spine = useRef<HTMLDivElement | null>(null)
  const indexRef = useRef(index)
  const drag = useRef<Drag | null>(null)
  const quietUntil = useRef(0)
  const mounted = useRef(false)

  // The track's transform is written here and only here — never from JSX —
  // so a finger mid-swipe and a prop-driven turn cannot overwrite each other.
  const place = (offsetPx: number, animate: boolean, idx = indexRef.current) => {
    const el = track.current
    if (!el) return
    el.style.transition = animate && !reducedMotion()
      ? `transform ${TURN_MS}ms ${TURN_EASE}` : 'none'
    el.style.transform = `translate3d(calc(${-idx * 100}% + ${offsetPx}px), 0, 0)`
    const s = spine.current
    if (s) {
      s.style.transition = offsetPx === 0 ? `opacity ${TURN_MS}ms ease-out` : 'none'
      s.style.opacity = String(Math.min(1, Math.abs(offsetPx) / 80))
      // The shadow sits on the edge the incoming page is arriving from.
      s.style.left = offsetPx < 0 ? '' : '0'
      s.style.right = offsetPx < 0 ? '0' : ''
    }
  }

  useLayoutEffect(() => {
    indexRef.current = index
    place(0, mounted.current)
    mounted.current = true
  }, [index])

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // A mouse drag is text selection, and mouse users have the tabs.
    if (!two || e.pointerType === 'mouse') return
    drag.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, axis: 'undecided', trail: [] }
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || e.pointerId !== d.id) return
    const dx = e.clientX - d.x0
    const dy = e.clientY - d.y0
    if (d.axis === 'undecided') {
      // Vertical first means a scroll: let go, the browser has it.
      if (Math.abs(dy) > AXIS_LOCK_PX && Math.abs(dy) >= Math.abs(dx)) { drag.current = null; return }
      if (Math.abs(dx) <= AXIS_LOCK_PX) return
      d.axis = 'x'
      try { viewport.current?.setPointerCapture(e.pointerId) } catch { /* already gone */ }
    }
    const idx = indexRef.current
    const past = (dx > 0 && idx === 0) || (dx < 0 && idx === PAGES.length - 1)
    d.dx = past ? dx * RUBBER : dx
    d.trail.push({ x: e.clientX, t: e.timeStamp })
    if (d.trail.length > 6) d.trail.shift()
    place(d.dx, false)
  }

  const settle = (e: ReactPointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const d = drag.current
    if (!d || e.pointerId !== d.id) return
    drag.current = null
    if (d.axis !== 'x') return
    quietUntil.current = e.timeStamp + CLICK_QUIET_MS

    let next = indexRef.current
    if (!cancelled) {
      const width = viewport.current?.clientWidth || 1
      const first = d.trail[0]
      const last = d.trail[d.trail.length - 1]
      const velocity = first && last && last.t > first.t ? (last.x - first.x) / (last.t - first.t) : 0
      if (d.dx < -width * TURN_FRACTION || velocity < -TURN_VELOCITY) next = Math.min(PAGES.length - 1, next + 1)
      else if (d.dx > width * TURN_FRACTION || velocity > TURN_VELOCITY) next = Math.max(0, next - 1)
    }
    place(0, true, next)
    if (next !== indexRef.current) onPage(PAGES[next]!)
  }

  // The finger that just turned the page must not also "tap" what it lands on.
  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.timeStamp < quietUntil.current) { e.stopPropagation(); e.preventDefault() }
  }

  return (
    <div
      ref={viewport}
      className="relative flex-1 min-h-0 overflow-hidden"
      style={{ touchAction: two ? 'pan-y' : undefined, overscrollBehaviorX: 'contain' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => settle(e, false)}
      onPointerCancel={(e) => settle(e, true)}
      onClickCapture={onClickCapture}
      data-testid="face-pager"
      data-page={two ? page : 'chat'}
    >
      <div ref={track} className="flex h-full w-full will-change-transform">
        {/* Page 1 — the conversation. Off-screen it is inert, so a keyboard
            cannot tab into what a sighted person cannot see. */}
        <div
          className="relative h-full w-full flex-none flex flex-col min-w-0"
          inert={two && index !== 0 ? true : undefined}
          aria-hidden={two && index !== 0 ? true : undefined}
        >
          {children}
          {two && (
            <button
              type="button"
              onClick={() => onPage('face')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-0.5 min-h-touch rounded-l-xl border border-r-0 border-brand-subtle bg-surface/95 px-1.5 py-2 text-brand shadow-md backdrop-blur hover:bg-brand-subtle"
              aria-label="Turn to Horton's face"
              title="Turn to Horton's face (or swipe left)"
              data-testid="pager-to-face"
            >
              <ScanFace className="w-4 h-4" aria-hidden="true" />
              <span className="text-app-micro font-medium leading-none">Face</span>
              <ChevronRight className="w-3 h-3 opacity-70" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Page 2 — the face. */}
        {two && (
          <div
            className="relative h-full w-full flex-none min-w-0"
            inert={index !== 1 ? true : undefined}
            aria-hidden={index !== 1 ? true : undefined}
          >
            {face}
            <button
              type="button"
              onClick={() => onPage('chat')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-0.5 min-h-touch rounded-r-xl bg-black/50 px-1.5 py-2 text-white/90 shadow-md backdrop-blur hover:bg-black/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              aria-label="Turn to the conversation"
              title="Turn to the conversation (or swipe right)"
              data-testid="pager-to-chat"
            >
              <ChevronLeft className="w-3 h-3 opacity-70" aria-hidden="true" />
              <MessageSquareText className="w-4 h-4" aria-hidden="true" />
              <span className="text-app-micro font-medium leading-none">Chat</span>
            </button>
          </div>
        )}
      </div>

      {/* The spine: a soft shadow along the edge a page is arriving from,
          while a finger is on it. Zero when settled. */}
      {two && (
        <div
          ref={spine}
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 w-6 bg-gradient-to-r from-black/20 to-transparent"
          style={{ opacity: 0 }}
        />
      )}
    </div>
  )
}
